import React, { Dispatch, FC, JSX, SetStateAction, useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { createPortal } from 'react-dom';

import { Button } from 'components/atoms';
import { BackIcon, DownloadIcon } from 'components/icons';
import { POST_REPARAPHRASE, PUT_DATAFRAME } from 'queries';
import downloadFile from 'utils/downloadFile';
import toastSuccess from 'utils/toastSuccess';
import { SearchList } from '../index';

import PATHS from 'routes/PATHS';
import { MessageResultType } from 'queries/types/MessageResultType';
import { ClassesModsType } from '../SearchList';

import './MessageResult.scss';

type MessageResultProps = {
  resultData: MessageResultType | null;
  setSwipeState: Dispatch<SetStateAction<boolean>>;
};

const MessageResult: FC<MessageResultProps> = ({ resultData, setSwipeState }) => {
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [rows, setRows] = useState<(string | number)[][]>([]);
  const [classesMods, setClassesMods] = useState<ClassesModsType>([]);
  const [data, setData] = useState<MessageResultType | null>(resultData);

  useEffect(() => {
    if (data) {
      generateEntries();
    }
  }, [data]);

  if (!data) return <Navigate to={PATHS.ROOT} />;

  const generateColumns = (): string[] => {
    if (!data?.dataframe) {
      return [];
    }

    const allValues = Object.values(data.dataframe);
    if (!allValues.length) {
      return [];
    }

    const firstItem = allValues[0];
    if (!firstItem || typeof firstItem !== 'object') {
      return [];
    }

    const columns = Object.keys(firstItem);
    columns.unshift('#');
    return columns;
  };

  const generateEntries = (reparaphraseItem?: {
    [x: string]: {
      [x: string]: string | number | JSX.Element;
    };
  }) => {
    if (!data?.dataframe) {
      return [];
    }

    const rows: (string | number)[][] = [];

    let reparaphraseId: number | null = null;
    let reparaphraseData: unknown = null;

    if (reparaphraseItem) {
      const [fullKey, data] = Object.entries(reparaphraseItem)[0];
      reparaphraseId = parseInt(fullKey, 10);
      reparaphraseData = data;
    }

    Object.entries(data.dataframe).forEach(([key, item]) => {
      const rowValues = Object.values(item);
      rowValues.unshift(Number(key));
      rows.push(rowValues);

      if (reparaphraseId !== null && Number(key) === reparaphraseId && reparaphraseData) {
        const extraRowValues = Object.values(reparaphraseData);
        extraRowValues.unshift(`${Number(key)}-new`);
        rows.push(extraRowValues);
        setClassesMods([
          { id: key, className: 'message-result__old-row' },
          { id: `${Number(key)}-new`, className: 'message-result__new-row' },
        ]);
      }
    });

    setRows(rows);
  };

  const getSearchKeys = (): string[] => {
    const columns = generateColumns();
    return columns.slice(1);
  };

  const handleRowClick = (item: Array<string | number | JSX.Element>) => {
    setLoadingState(true);
    if (classesMods.find((elem) => elem.id === item[0].toString())) {
      updateDataframe(item);
    } else {
      setClassesMods([]);
      reparaphrase(item);
    }
  };

  const updateDataframe = (item: Array<string | number | JSX.Element>) => {
    const columns = generateColumns();
    columns.shift();

    const dataframeFields = columns.reduce<Record<string, string | number | JSX.Element>>(
      (acc, column, idx) => {
        acc[column] = item[idx + 1];
        return acc;
      },
      {},
    );

    const preparedData = {
      dataframe_id: data.id,
      dataframe_series: {
        [`${item[0]}`]: {
          ...dataframeFields,
        },
      },
    };

    PUT_DATAFRAME(preparedData).then((result) => {
      setLoadingState(false);
      if (result.success) {
        setLoadingState(false);
        toastSuccess(`Successfully updated data!`);
        setClassesMods([]);
        const newData = result.data && 'data' in result.data ? result.data.data : null;
        setData(newData);
      }
    });
  };

  const reparaphrase = (item: Array<string | number | JSX.Element>) => {
    POST_REPARAPHRASE({ dataframe_id: data.id.toString(), row_index: item[0].toString() }).then(
      (result) => {
        setLoadingState(false);
        if (result.success) {
          const dataframe =
            result.data && 'data' in result.data ? result.data.data.dataframe : undefined;
          generateEntries(dataframe);
        }
      },
    );
  };

  return (
    <div className={'message-result'}>
      {createPortal(
        <div className={'message-result__control'} style={{ opacity: data ? 1 : 0 }}>
          <Button
            className={'message-result__control-item'}
            primary
            onClick={() => setSwipeState(false)}
          >
            <BackIcon />
          </Button>
          <Button
            onClick={() => downloadFile(data?.excel_file, data?.file_name)}
            className={'message-result__control-item'}
          >
            <DownloadIcon />
          </Button>
        </div>,
        document.getElementById('message') || document.body,
      )}
      <SearchList
        rows={rows}
        columns={generateColumns()}
        searchBy={getSearchKeys()}
        sortBy="All"
        rowClickFunc={(item) => !loadingState && handleRowClick(item)}
        loading={loadingState}
        classesMods={classesMods}
      />
    </div>
  );
};

export default MessageResult;
